import React, { useRef } from 'react'

import Button from 'react-bootstrap/Button'
import Modal from 'react-bootstrap/Modal'
import { QRCodeCanvas } from 'qrcode.react'

export function buildLocationMenuUrl(organizationUuid, locationId) {
  const base = (process.env.REACT_APP_CONSUMER_PORTAL || '').replace(/\/$/, '')
  const menuKey = encodeURIComponent(String(locationId ?? '').trim())
  return `${base}/menu/${organizationUuid}/${menuKey}`
}

export function buildSeatDeliveryMenuUrl(
  organizationUuid,
  venueUuid,
  sectionName
) {
  const base = (process.env.REACT_APP_CONSUMER_PORTAL || '').replace(/\/$/, '')
  const venue = encodeURIComponent(String(venueUuid ?? '').trim())
  const section = encodeURIComponent(String(sectionName ?? '').trim())
  return `${base}/menu/${organizationUuid}/${venue}/${section}`
}

function escapeHtml(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

function getQrDataUrl(ref) {
  const canvas = ref.current?.querySelector('canvas')
  return canvas?.toDataURL('image/png') ?? ''
}

function QrSection({ title, hint, url, qrRef }) {
  if (!url) return null

  return (
    <section className="location-qr-modal__section">
      <h3 className="location-qr-modal__section-title">{title}</h3>
      <p className="location-qr-modal__section-hint">{hint}</p>
      <div className="location-qr-modal__code-wrap" ref={qrRef}>
        <QRCodeCanvas
          value={url}
          size={220}
          includeMargin
          marginSize={2}
          level="M"
        />
      </div>
      <p className="location-qr-modal__url">{url}</p>
    </section>
  )
}

export default function LocationQrModal({
  show,
  onHide,
  locationName,
  menuUrl = '',
  seatDeliveryUrl = '',
}) {
  const menuQrRef = useRef(null)
  const seatDeliveryQrRef = useRef(null)

  const handleExport = (ref, slug) => {
    const dataUrl = getQrDataUrl(ref)
    if (!dataUrl) return

    const anchor = document.createElement('a')
    anchor.download = `${locationName || 'location'}-${slug}-qr.png`
    anchor.href = dataUrl
    anchor.click()
  }

  const handlePrint = () => {
    const menuDataUrl = menuUrl ? getQrDataUrl(menuQrRef) : ''
    const seatDataUrl = seatDeliveryUrl ? getQrDataUrl(seatDeliveryQrRef) : ''
    if (!menuDataUrl && !seatDataUrl) return

    const printWindow = window.open('', '_blank', 'noopener,noreferrer')
    if (!printWindow) return

    const safeName = escapeHtml(locationName)
    const sections = []

    if (menuDataUrl) {
      sections.push(`
        <section class="qr-block">
          <h2>Location menu</h2>
          <p class="subtitle">Scan to open the menu for this location</p>
          <img src="${menuDataUrl}" alt="Location menu QR code for ${safeName}" />
          <p class="url">${escapeHtml(menuUrl)}</p>
        </section>`)
    }

    if (seatDataUrl) {
      sections.push(`
        <section class="qr-block">
          <h2>Seat delivery</h2>
          <p class="subtitle">Scan to order seat delivery for this section</p>
          <img src="${seatDataUrl}" alt="Seat-delivery QR code for ${safeName}" />
          <p class="url">${escapeHtml(seatDeliveryUrl)}</p>
        </section>`)
    }

    printWindow.document.write(`<!DOCTYPE html>
<html lang="en">
  <head>
    <meta charset="utf-8" />
    <title>${safeName} — Menu QR codes</title>
    <style>
      @page { margin: 0.75in; }
      body {
        font-family: system-ui, -apple-system, sans-serif;
        text-align: center;
        padding: 32px 24px;
        color: #111827;
      }
      h1 {
        font-size: 28px;
        font-weight: 600;
        margin: 0 0 32px;
      }
      .qr-block {
        page-break-inside: avoid;
        margin-bottom: 48px;
      }
      .qr-block:last-child {
        margin-bottom: 0;
      }
      h2 {
        font-size: 20px;
        font-weight: 600;
        margin: 0 0 8px;
      }
      .subtitle {
        color: #6b7280;
        margin: 0 0 24px;
        font-size: 16px;
      }
      img {
        width: 240px;
        height: 240px;
      }
      .url {
        margin-top: 24px;
        font-size: 12px;
        color: #6b7280;
        word-break: break-all;
      }
    </style>
  </head>
  <body>
    <h1>${safeName}</h1>
    ${sections.join('')}
    <script>
      window.onload = function () {
        window.print();
        window.onafterprint = function () { window.close(); };
      };
    </script>
  </body>
</html>`)
    printWindow.document.close()
  }

  const canPrint = Boolean(menuUrl || seatDeliveryUrl)

  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      animation={false}
      className="location-qr-modal"
    >
      <Modal.Header closeButton>
        <Modal.Title>Location QR codes</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="location-qr-modal__heading">
          <h2 className="location-qr-modal__name">{locationName}</h2>
          <p className="location-qr-modal__hint">
            Each code opens a different menu link for this location.
          </p>
        </div>

        <QrSection
          title="Location menu"
          hint="Opens the menu for pickup at this stand — no seat info required."
          url={menuUrl}
          qrRef={menuQrRef}
        />

        {seatDeliveryUrl ? (
          <QrSection
            title="Seat delivery"
            hint="Opens the menu for seat-delivery orders in this section."
            url={seatDeliveryUrl}
            qrRef={seatDeliveryQrRef}
          />
        ) : (
          <p className="location-qr-modal__section-hint location-qr-modal__section-hint--standalone">
            Assign a venue and section to this location to generate a
            seat-delivery QR code.
          </p>
        )}
      </Modal.Body>
      <Modal.Footer className="location-qr-modal__footer">
        <Button variant="secondary" onClick={onHide}>
          Close
        </Button>
        <Button
          variant="outline-primary"
          onClick={() => handleExport(menuQrRef, 'menu')}
          disabled={!menuUrl}
        >
          Download menu QR
        </Button>
        <Button
          variant="outline-primary"
          onClick={() => handleExport(seatDeliveryQrRef, 'seat-delivery')}
          disabled={!seatDeliveryUrl}
        >
          Download seat-delivery QR
        </Button>
        <Button variant="primary" onClick={handlePrint} disabled={!canPrint}>
          Print
        </Button>
      </Modal.Footer>
    </Modal>
  )
}
